import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCountry } from "@/contexts/country";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import CountryPollsTab from "@/components/polls/CountryPollsTab";
import CountryHero from "@/components/country/CountryHero";
import CountryFeaturedVideo from "@/components/country/CountryFeaturedVideo";
import InsightsCTA from "@/components/insights/InsightsCTA";
import SuggestPollModal from "@/components/polls/SuggestPollModal";
import { Button } from "@/components/ui/button";
import { LightbulbIcon } from "lucide-react";
import CountrySocialLinks from "@/components/country/CountrySocialLinks";
import UnifiedBreadcrumbs from "@/components/navigation/UnifiedBreadcrumbs";
import ResourceNavigationTabs from "@/components/navigation/ResourceNavigationTabs";
import { useUnifiedNavigation } from "@/hooks/navigation/useUnifiedNavigation";

// Import generic sections that work for all countries
import HowItWorksSection from "@/components/sections/HowItWorksSection";
import NationalSentimentDashboard from "@/components/country/NationalSentimentDashboard";
import FeaturedInsightsCarousel from "@/components/country/FeaturedInsightsCarousel";
import UnderstandingCards from "@/components/country/UnderstandingCards";
import JoinMovementSection from "@/components/country/JoinMovementSection";
import ResourcesGrid from "@/components/country/ResourcesGrid";

const CountryCards = () => {
  const { countryCode } = useParams();
  const navigate = useNavigate();
  const {
    availableCountries,
    setCountry,
    countryName,
    isLoading,
    selectedCountry,
  } = useCountry();
  const [showSuggestPoll, setShowSuggestPoll] = useState(false);
  const { handleCountryChange, generateBreadcrumbs } = useUnifiedNavigation();

  useEffect(() => {
    if (countryCode && !isLoading) {
      const isValidCountry = availableCountries.some(
        (country) => country.code === countryCode && country.active
      );

      if (isValidCountry) {
        setCountry(countryCode);
      } else {
        toast.error("Country not found or not available");
        navigate("/countries");
      }
    }
  }, [countryCode, availableCountries, setCountry, navigate, isLoading]);

  const currentCountry = availableCountries.find((c) => c.code === countryCode);

  const breadcrumbs = generateBreadcrumbs("insights", countryCode, countryName);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 pt-[84px]">
        <CountryHero
          countryName={countryName || "Country"}
          backgroundImageUrl={currentCountry?.background_image_url}
          backgroundImageAlt={currentCountry?.background_image_alt}
          showSelector={false}
        />

        {/* Navigation */}
        <div className="container mx-auto px-4 max-w-screen-xl py-6">
          <ResourceNavigationTabs
            countryCode={countryCode}
            countryName={countryName}
            showCountrySelector={true}
            availableCountries={availableCountries}
            onCountryChange={handleCountryChange}
          />
        </div>

        {/* Featured Video - New section placed after the hero but before polls section */}
        {selectedCountry?.featured_video &&
          selectedCountry.featured_video.enabled && (
            <CountryFeaturedVideo
              videoConfig={selectedCountry.featured_video}
            />
          )}

        {/* Polls section */}
        <section className="py-12 bg-gradient-to-br from-background via-secondary/5 to-background">
          <div className="container mx-auto px-4 max-w-none">
            <CountryPollsTab countryName={countryName} />

            {/* Poll suggestion button */}
            <div className="text-center mt-8">
              <Button
                onClick={() => setShowSuggestPoll(true)}
                variant="outline"
                size="lg"
                className="gap-3 bg-background/80 backdrop-blur-sm border-border/50 hover:bg-secondary/80 shadow-sm"
              >
                <LightbulbIcon size={18} />
                <span className="font-medium">Suggest a Poll</span>
              </Button>
            </div>
          </div>
        </section>

        {/* Country-specific sections */}
        <NationalSentimentDashboard />
        <UnderstandingCards />
        <HowItWorksSection />
        <FeaturedInsightsCarousel />

        {/* Join the Movement Section */}
        <JoinMovementSection />

        {/* Resources Grid */}
        <ResourcesGrid />

        {/* Social Media Section */}
        {selectedCountry?.social_media &&
          selectedCountry.social_media.length > 0 && (
            <CountrySocialLinks
              countryName={countryName || "this country"}
              socialPlatforms={selectedCountry.social_media}
            />
          )}

        {/* Call to action */}
        <InsightsCTA countryName={countryName || "this country"} />
      </main>

      {/* Poll suggestion modal */}
      <SuggestPollModal
        open={showSuggestPoll}
        onClose={() => setShowSuggestPoll(false)}
      />

      <Footer />
      <BackToTop />
    </div>
  );
};

export default CountryCards;
